import { MaxLengthValidatorDirective } from '../../shared/directives/max-length-validator.directive';
import { Component, OnInit, inject } from '@angular/core';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { PageTitleService } from '../../core/services/page-title.service';
import { ProductService } from '../../core/services/product.service';
import { TaxRateService } from '../../core/services/tax-rate.service';
import { InvoiceService } from '../../core/services/invoice.service';
import { FlashService } from '../../core/services/flash.service';
import { FlashMessageComponent } from '../../shared/components/flash-message.component';

const PRODUCT_TYPES = [
  { id: 0, name: 'その他' }, { id: 1, name: '月会費' }, { id: 2, name: '入会金・事務手数料' },
  { id: 3, name: 'ロッカー費' }, { id: 4, name: 'PT費' }, { id: 5, name: '物販' },
];

@Component({
    selector: 'app-product-edit',
    imports: [MaxLengthValidatorDirective, RouterLink, FormsModule, FlashMessageComponent],
    template: `
<div class="animated fadeIn">
  <div class="row sfapp-container">
    <div class="col-md-12">
      <div class="card">
        <div class="card-header">
          <span><i class="fa s-icon-edit"></i>&nbsp;編集</span>
        </div>
        <div class="card-body">
          <div class="row"><app-flash-message class="col-md-12"></app-flash-message></div>
          <form novalidate name="productEditForm" #productEditForm="ngForm" (ngSubmit)="save(productEditForm)">
            <div class="row">
              <div class="col-md-12">
                <div class="form-group row">
                  <div class="col-md-4">
                    <label class="col-form-label">商品種別</label>
                    <select class="form-control" [(ngModel)]="selectProductType" name="productType" required>
                      <option [ngValue]="null">選択してください</option>
                      @for (pt of productTypes; track pt.id) {
                        <option [ngValue]="pt">{{pt.name}}</option>
                      }
                    </select>
                  </div>
                </div>
                <div class="form-group row">
                  <div class="col-md-8">
                    <label class="col-form-label">商品名</label>
                    <input type="text" class="form-control" [(ngModel)]="product.name" name="name" sfMaxLength="200">
                  </div>
                </div>
                <div class="form-group row">
                  <div class="col-md-4">
                    <label class="col-form-label">金額</label>
                    <input type="number" class="form-control" [(ngModel)]="product.amount" name="amount" required>
                  </div>
                  <div class="col-md-4">
                    <label class="col-form-label">消費税率</label>
                    <select class="form-control" [(ngModel)]="selectTaxRate" name="taxRate" required>
                      <option [ngValue]="null">選択してください</option>
                      @for (tr of taxRates; track tr.id) {
                        <option [ngValue]="tr">{{(tr.rate * 100)}}%</option>
                      }
                    </select>
                  </div>
                </div>
                <div class="form-group row">
                  <div class="col-md-8">
                    <label class="col-form-label">備考</label>
                    <input type="text" class="form-control" [(ngModel)]="product.note" name="note" sfMaxLength="200">
                  </div>
                </div>
              </div>
            </div>
            <div class="form-group row">
              <div class="col-md-12 text-right">
                <a routerLink="/product" class="btn bg-close text-white"><i class="fa s-icon-cancel"></i>&nbsp;閉じる</a>
                <button type="submit" class="btn btn-success" [disabled]="saving || productEditForm.invalid || isInvalid()"><i class="fa s-icon-save"></i>&nbsp;保存</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</div>
  `
})
export class ProductEditComponent implements OnInit {
  private pageTitleService = inject(PageTitleService);
  private productService = inject(ProductService);
  private taxRateService = inject(TaxRateService);
  private invoiceService = inject(InvoiceService);
  private route = inject(ActivatedRoute);
  private flash = inject(FlashService);

  product: any = {};
  taxRates: any[] = [];
  selectProductType: any = null;
  selectTaxRate: any = null;
  saving = false;
  readonly productTypes = PRODUCT_TYPES;

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('商品マスター', 's-icon-edit');
    await this.loadProduct();
  }

  private async loadProduct(): Promise<void> {
    const id = +this.route.snapshot.params['productId'];
    const [data, taxRates] = await Promise.all([
      this.productService.getOne(id),
      this.taxRateService.get(),
    ]);
    this.taxRates = taxRates ?? [];
    this.product = data ?? { id };
    this.selectProductType = PRODUCT_TYPES.find(pt => pt.id === this.product.productType) ?? null;
    this.selectTaxRate = this.taxRates.find((tr: any) => tr.id === this.product.taxRateId) ?? null;
  }

  isInvalid(): boolean {
    return !this.selectProductType || this.product.amount === null || this.product.amount === undefined || this.product.amount === '' ||
      !this.selectTaxRate;
  }

  async save(form?: NgForm): Promise<void> {
    if (form?.invalid || this.isInvalid()) {
      form?.control.markAllAsTouched();
      return;
    }
    this.saving = true;
    try {
      const json = { ...this.product };
      json.productType = this.selectProductType?.id;
      json.taxRateId = this.selectTaxRate?.id;
      delete json['status'];
      delete json['deletedAt'];
      delete json['createdAt'];
      delete json['updatedAt'];
      const saved: any = await this.productService.upsert(json);
      if (!saved?.id) return;
      // 旧版 product.js:245-268: 請求再計算が偽ならdanger、保存成功は同じ編集画面を再表示し先頭のFlashで通知
      const txId: any = await this.productService.updateTransactionAmount(saved.id);
      let recalculationFailed = false;
      if (txId) {
        recalculationFailed = !(await this.invoiceService.recalculationAmount({ id: txId }));
      }
      this.flash.create('success', '商品情報を更新しました');
      if (recalculationFailed) this.flash.create('danger', '請求情報の更新に失敗しました');
      window.scrollTo(0, 0);
      await this.loadProduct();
    } finally {
      this.saving = false;
    }
  }
}
